/**
 * Typed hooks for the /presentations API.
 *
 * A Presentation is a self-contained HTML deck with immutable published
 * versions. Unlike a Report it takes no parameters and has no render
 * pipeline — the current version's HTML is served verbatim by the
 * backend's `/presentations/:id/view` (authenticated) and
 * `/kiosk/presentations/:id/view?token=` (public share) endpoints.
 *
 * Rendering in the SPA mirrors the report-render pattern: we fetch the
 * HTML document with the bearer token via `fetchRaw`, wrap it in a Blob
 * URL, and point an <iframe> at that URL — an iframe `src` can't carry
 * an Authorization header, so the blob hop is what makes the
 * authenticated viewer work.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

export type PresentationAudience = 'internal' | 'customer' | 'external';

export interface PresentationSummary {
  id: string;
  tenantId: string;
  folderId: string | null;
  name: string;
  description: string | null;
  audience: PresentationAudience;
  currentVersionId: string | null;
  ownerId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PresentationVersion {
  id: string;
  presentationId: string;
  tenantId: string;
  version: number;
  html: string;
  metadata: Record<string, unknown>;
  publishedBy: string | null;
  publishedAt: string;
}

export interface PresentationWithCurrent extends PresentationSummary {
  currentVersion: PresentationVersion | null;
}

export interface PresentationShareToken {
  id: string;
  presentationId: string;
  tenantId: string;
  tokenPrefix: string;
  label: string | null;
  audience: PresentationAudience;
  createdBy: string | null;
  createdAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
}

/** Mint response only — the plaintext `token` is never re-readable. */
export interface MintedShareToken extends PresentationShareToken {
  token: string;
}

export function usePresentations(filters?: { folderId?: string | null; query?: string }) {
  const api = useApi();
  const params = new URLSearchParams();
  if (filters?.folderId !== undefined && filters.folderId !== null) {
    params.set('folderId', filters.folderId);
  }
  if (filters?.query) params.set('query', filters.query);
  const qs = params.toString();
  return useQuery<PresentationSummary[]>({
    queryKey: ['presentations', filters?.folderId ?? 'any', filters?.query ?? ''],
    queryFn: () => api.fetch(`/presentations${qs ? `?${qs}` : ''}`),
  });
}

export function usePresentation(id: string | null) {
  const api = useApi();
  return useQuery<PresentationWithCurrent>({
    queryKey: ['presentations', 'one', id],
    queryFn: () => api.fetch(`/presentations/${id}`),
    enabled: !!id,
  });
}

export interface ValidationIssue {
  severity: 'error' | 'warning';
  code: string;
  message: string;
}
export interface ValidationResult {
  ok: boolean;
  issues: ValidationIssue[];
  stats: { bytes: number; approxSlideCount: number; hasTitle: boolean };
}

/**
 * Validate-only call. Same checks the server runs on create/publish,
 * without persisting. Use from the editor's "Validate" button before
 * Save & Publish so authors see the real backend verdict — not just
 * the in-browser inliner's local heuristics.
 */
export function useValidatePresentation() {
  const api = useApi();
  return useMutation({
    mutationFn: (html: string) =>
      api.fetch<ValidationResult>(`/presentations/validate`, {
        method: 'POST',
        body: JSON.stringify({ html }),
      }),
  });
}

export interface CreatePresentationInput {
  name: string;
  audience: PresentationAudience;
  html: string;
  description?: string | null;
  folderId?: string | null;
  metadata?: Record<string, unknown>;
}

/**
 * Creates a new presentation and publishes its v1 HTML atomically.
 * Server runs validatePresentationHtml first; a failed validation
 * throws (the editor should call useValidatePresentation first to
 * surface issues inline).
 */
export function useCreatePresentation() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePresentationInput) =>
      api.fetch<PresentationWithCurrent>(`/presentations`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['presentations'] }),
  });
}

/**
 * Appends a new immutable version of the HTML. The viewer flips to
 * the new version immediately for both internal viewers and any
 * outstanding share tokens.
 */
export function usePublishPresentationVersion() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      html,
      metadata,
    }: {
      id: string;
      html: string;
      metadata?: Record<string, unknown>;
    }) =>
      api.fetch<PresentationVersion>(`/presentations/${id}/versions`, {
        method: 'POST',
        body: JSON.stringify({ html, ...(metadata ? { metadata } : {}) }),
      }),
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: ['presentations'] });
      void qc.invalidateQueries({ queryKey: ['presentations', 'one', id] });
    },
  });
}

export function useUpdatePresentation() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: {
        name?: string;
        description?: string | null;
        audience?: PresentationAudience;
        folderId?: string | null;
      };
    }) =>
      api.fetch<PresentationSummary>(`/presentations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: ['presentations'] });
      void qc.invalidateQueries({ queryKey: ['presentations', 'one', id] });
    },
  });
}

export function useDeletePresentation() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      // 204 No Content — fetchRaw so we don't try to JSON-parse an empty body.
      api.fetchRaw(`/presentations/${id}`, { method: 'DELETE' }).then((r) => {
        if (!r.ok) throw new Error(`Delete failed (${r.status})`);
        return { ok: true as const };
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['presentations'] }),
  });
}

// ─── Share tokens ───────────────────────────────────────────────────────

export function usePresentationShares(id: string | null) {
  const api = useApi();
  return useQuery<PresentationShareToken[]>({
    queryKey: ['presentations', 'shares', id],
    queryFn: () => api.fetch(`/presentations/${id}/shares`),
    enabled: !!id,
  });
}

export function useMintPresentationShare() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, label, expiresAt }: { id: string; label?: string; expiresAt?: string }) =>
      api.fetch<MintedShareToken>(`/presentations/${id}/shares`, {
        method: 'POST',
        body: JSON.stringify({
          ...(label ? { label } : {}),
          ...(expiresAt ? { expiresAt } : {}),
        }),
      }),
    onSuccess: (_, { id }) =>
      void qc.invalidateQueries({ queryKey: ['presentations', 'shares', id] }),
  });
}

export function useRevokePresentationShare() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tokenId }: { id: string; tokenId: string }) =>
      api.fetchRaw(`/presentations/${id}/shares/${tokenId}`, { method: 'DELETE' }).then((r) => {
        if (!r.ok) throw new Error(`Revoke failed (${r.status})`);
        return { ok: true as const };
      }),
    onSuccess: (_, { id }) =>
      void qc.invalidateQueries({ queryKey: ['presentations', 'shares', id] }),
  });
}

/**
 * Fetch the authenticated viewer HTML and wrap it in a Blob URL the
 * iframe can load. Returns the object URL plus a disposer the caller
 * must invoke (URL.revokeObjectURL) when swapping decks or unmounting.
 * Throws with the server's message on a non-2xx (e.g. no published
 * version yet).
 */
export async function fetchPresentationViewBlob(
  fetchRaw: (path: string, init?: RequestInit) => Promise<Response>,
  id: string,
): Promise<string> {
  const res = await fetchRaw(`/presentations/${id}/view`);
  if (!res.ok) {
    let message = `Failed to load presentation (${res.status})`;
    try {
      const body = (await res.json()) as { message?: string };
      if (body.message) message = body.message;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(message);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}
